import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import FlowBuilderCanvas from "./FlowBuilderCanvas";
import "./FlowCreatorPage.css";

/**
 * Full-screen, full-width page for creating / editing an engagement flow.
 * Rendered through a portal to document.body so it escapes any ancestor
 * transform/overflow constraints (which previously cut the modal off) and
 * can use the entire viewport width.
 */
const FlowCreatorPage = ({
  flowName,
  onFlowNameChange,
  trigger,
  onTriggerChange,
  triggerValue,
  onTriggerValueChange,
  triggerOptions,
  allTouchpoints,
  visualFlowData,
  customTouchpoints,
  flowType,
  onFlowTypeChange,
  emailTopics,
  senderEmails,
  onSave,
  onClose,
}) => {
  // Ask whether to save before leaving the builder.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const requestClose = () => setShowCloseConfirm(true);

  const dispatchSave = () => {
    document.dispatchEvent(new CustomEvent("saveFlow"));
  };

  const handleSaveAndClose = () => {
    setShowCloseConfirm(false);
    dispatchSave(); // handleSaveVisualFlow persists and closes the builder
  };

  const handleDiscardAndClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  // Close on Escape and lock background scroll while the page is open.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setShowCloseConfirm((open) => {
        if (open) return false; // Escape closes the confirm dialog first
        return true; // otherwise ask before leaving
      });
    };
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div className="flowCreatorPage">
      <div className="flowCreatorHeader">
        <div className="flowCreatorTitle">
          <span className="flowCreatorBrandIcon">
            <img src="/img/star.svg" alt="SalesGod" />
          </span>
          <div className="flowCreatorTitleText">
            <span className="flowCreatorBrand">SalesGod Flows</span>
            <input
              type="text"
              className={`flowCreatorNameInput ${!flowName.trim() ? "empty" : ""}`}
              value={flowName}
              onChange={(e) => onFlowNameChange(e.target.value)}
              placeholder="Enter flow name..."
            />
          </div>
        </div>
        <div className="flowCreatorActions">
          <button
            className="flowCreatorSaveBtn"
            onClick={dispatchSave}
            disabled={!flowName.trim()}
          >
            <i className="fa fa-save"></i>
            Save
          </button>
          <button className="flowCreatorClose" onClick={requestClose} title="Close">
            <i className="fa fa-times"></i>
          </button>
        </div>
      </div>

      {/* Compact Trigger Bar */}
      <div className="flowCreatorTriggerBar">
        <span className="triggerLabel">Type:</span>
        <div className="flowTypePills" role="group" aria-label="Flow type">
          <button
            type="button"
            className={`flowTypePill ${flowType === "warmup" ? "active warmup" : ""}`}
            onClick={() => onFlowTypeChange && onFlowTypeChange("warmup")}
          >
            <i className="fa fa-fire"></i>
            Warmup
          </button>
          <button
            type="button"
            className={`flowTypePill ${flowType === "retargeting" ? "active retargeting" : ""}`}
            onClick={() => onFlowTypeChange && onFlowTypeChange("retargeting")}
          >
            <i className="fa fa-redo"></i>
            Retargeting
          </button>
        </div>

        <span className="triggerBarDivider" aria-hidden="true"></span>

        <span className="triggerLabel">Trigger:</span>
        <select
          className="triggerSelect"
          value={trigger}
          onChange={(e) => {
            onTriggerChange(e.target.value);
            onTriggerValueChange("");
          }}
        >
          {Object.entries(triggerOptions).map(([key, opt]) => (
            <option key={key} value={key}>
              {opt.label}
            </option>
          ))}
        </select>

        {trigger === "touchpoint_reached" && (
          <>
            <span className="triggerLabel">after</span>
            <select
              className="triggerSelect"
              value={triggerValue}
              onChange={(e) => onTriggerValueChange(e.target.value)}
            >
              <option value="">Select touchpoint...</option>
              {Object.entries(allTouchpoints).map(([key, tp]) => (
                <option key={key} value={key}>
                  {tp.label}
                </option>
              ))}
            </select>
          </>
        )}

        {trigger === "score_reached" && (
          <>
            <span className="triggerLabel">at</span>
            <input
              type="number"
              className="triggerScoreInput"
              min="1"
              max="20"
              value={triggerValue}
              onChange={(e) => onTriggerValueChange(e.target.value)}
              placeholder="10"
            />
            <span className="triggerLabel">/ 20 pts</span>
          </>
        )}
      </div>

      <div className="flowCreatorCanvas">
        <FlowBuilderCanvas
          initialNodes={visualFlowData.nodes}
          initialEdges={visualFlowData.edges}
          flowName={flowName}
          onFlowNameChange={onFlowNameChange}
          onFlowChange={onSave}
          customTouchpoints={customTouchpoints}
          triggerLabel={triggerOptions[trigger]?.label || "Start"}
          flowType={flowType}
          emailTopics={emailTopics}
          senderEmails={senderEmails}
        />
      </div>

      {showCloseConfirm && (
        <div className="flowCloseConfirmOverlay" onClick={() => setShowCloseConfirm(false)}>
          <div className="flowCloseConfirmCard" onClick={(e) => e.stopPropagation()}>
            <div className="flowCloseConfirmIcon">
              <i className="fa fa-save"></i>
            </div>
            <h3 className="flowCloseConfirmTitle">Save this flow?</h3>
            <p className="flowCloseConfirmText">
              Do you want to save your changes before leaving the flow builder?
            </p>
            <div className="flowCloseConfirmActions">
              <button
                className="flowCloseConfirmSave"
                onClick={handleSaveAndClose}
                disabled={!flowName.trim()}
                title={!flowName.trim() ? "Add a flow name first" : "Save and close"}
              >
                <i className="fa fa-save"></i> Save &amp; close
              </button>
              <button className="flowCloseConfirmDiscard" onClick={handleDiscardAndClose}>
                Discard
              </button>
              <button className="flowCloseConfirmCancel" onClick={() => setShowCloseConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FlowCreatorPage;
